import moment from 'moment';
// import * as XLSX from 'xlsx-js-style'
import { getIsErrorValue, buildExcelColumns, setExportExcelDefaultStyle, setExportExcelDefaultColWidths } from '@/pages/dashboard/utils/utils';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';

type CellType = 's' | 'n' | 'b' | 'd';

function getCellType(type = 'string'): CellType {
  const typeMap: Record<string, CellType> = {
    string: 's',
    number: 'n',
    boolean: 'b',
    date: 'd',
  };
  return typeMap[type] || 's';
}

/** 百分比导出入参：原始数值 / 空值 */
type TPercentCellValue = string | number | null | undefined;

/** 百分比导出单元格（xlsx cell） */
type TPercentExportCell = {
  t: CellType;
  v: string | number;
  z: string;
};

/**
 * 百分比列导出：与表格展示对齐
 * - 空值 → '-'
 * - 超过展示上限 / Infinity → '999.9%+'
 * - 正常值仍按 Excel 百分比数字格式写出
 */
function formatPercentExportCell(
  cellValue: TPercentCellValue,
  cellFormat: string,
): TPercentExportCell {
  const formatRule = /0\.00+%/.test(cellFormat) ? 'percent2Decimal' : 'percent1Decimal';
  const display = dataFormat({ value: cellValue ?? '', formatRule });

  if (display === '-' || (typeof display === 'string' && display.includes('%+'))) {
    return { t: 's', v: display, z: '@' };
  }

  // xlsx 无法序列化 Infinity/NaN，兜底为展示文案
  if (!Number.isFinite(Number(cellValue))) {
    return { t: 's', v: typeof display === 'string' ? display : '-', z: '@' };
  }

  return { t: 'n', v: Number(cellValue), z: cellFormat };
}

export async function exportExcel(fileName: string, table: any, heads = []) {
  fileName = fileName?.replace(/\u0020/g, '_');

  // 自定义单元格格式
  const data = exportExcel.formatData(table, heads);

  // 设置默认样式
  setExportExcelDefaultStyle(data);

  const XLSX = await import('xlsx-js-style');

  // 创建book
  const wb = XLSX.utils.book_new();

  // json转sheet
  const ws = XLSX.utils.json_to_sheet(data, { header: [], skipHeader: true });

  // 设置列宽
  setExportExcelDefaultColWidths(ws, data);

  // 下载时操作系统不允许输入半角冒号，所以用全角的代替
  const timestamp = moment().format('YYYY_MM_DD HH_mm_ss');

  // sheet写入book
  XLSX.utils.book_append_sheet(wb, ws, 'file');

  // 输出
  XLSX.writeFile(wb, `${fileName} ${timestamp}.xlsx`);
}

exportExcel.formatData = function (table: any, heads: any) {
  if (!heads.length) return table;

  const data = [];
  const letters = buildExcelColumns(heads?.length ?? 0); // 解决数字首位的列名乱序问题
  // @ts-ignore
  const row = heads?.reduce((o, { header, name }, i) => ((o[letters[i]] = header ?? name), o), {}) ?? {};
  Object.keys(row).length && data.push(row);

  for (let i = 0, l = table.length;i < l;i++) {
    const row = table[i];
    const newRow: any = {};
    
    // @ts-ignore
    heads?.forEach(({ key, type, format }, ii) => {
      const cellFormat = format || '';

      const dataType = getCellType(type);
      const cellValue = row[key];

      switch (dataType) {
        case 'n':
          if (typeof cellFormat === 'string' && cellFormat.includes('%')) {
            newRow[letters[ii]] = formatPercentExportCell(cellValue, cellFormat);
          } else {
            newRow[letters[ii]] = {
              t: getIsErrorValue(cellValue) ? 's' : dataType,
              v: getIsErrorValue(cellValue) ? '-' : cellValue,
              z: cellFormat,
            };
          }
          break;
        case 'd':
          newRow[letters[ii]] = { t: 's', v: cellValue, z: cellFormat };
          break;
        case 's':
          newRow[letters[ii]] = { t: 's', v: cellValue, z: '@' };
          break;
        default:
          newRow[letters[ii]] = { v: cellValue };
      }
    });
    

    data.length && data.push(newRow);
  }

  return data;
};
